import React, { forwardRef, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { postProductInquiryCall } from '@/api/public';
import { ChevronLeftIcon, PencilLineSoildIcon, PhoneRingOutlineIcon, TrashIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import {
  CONTRACT_STATUS_BEFORE,
  CONTRACT_STATUS_COMPLETED,
  CONTRACT_STATUS_IN_PROGRESS,
  TRANSFEREE,
  TRANSFEROR,
} from '@/const/contract';
import { SALE } from '@/const/productStatus';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { useMemberContext } from '@/contexts/Member/MemberContext';
import useNavigateBack from '@/hooks/useNavigateBack';
import { formatPrice, phoneCall, truncateText } from '@/utils/common';

type HeaderProps = {
  chatRoomData: ChattingDetailResponse | undefined;
  onClickDeleteBtn: () => void;
  path?: string;
  actionFunction?: (() => void) | undefined;
};

const ChatRoomHeader = forwardRef<HTMLDivElement, HeaderProps>(
  ({ chatRoomData, onClickDeleteBtn, path, actionFunction }, ref) => {
    const navigate = useNavigate();
    const navigateBack = useNavigateBack();
    const { member } = useMemberContext();
    const { showToast } = useToastContext();
    const [contractWriterType, setContractWriterType] = useState<string>('');
    const [contractStatus, setContractStatus] = useState('');

    const isBuyer = member?.id === chatRoomData?.buyer?.id;
    const recipient = isBuyer ? chatRoomData?.seller : chatRoomData?.buyer;

    const isProductDeleted = () => chatRoomData?.isDeletedProduct;

    const onClickPhoneCall = () => {
      if (chatRoomData?.id) {
        postProductInquiryCallMutation.mutate(chatRoomData.productId);
        const phoneNumber = chatRoomData?.safetyNumbers?.safetyNumber;
        phoneCall(phoneNumber);
      } else {
        showToast(
          `${truncateText(recipient?.name, 3)}님에게 메시지를 보낸 후, 전화를 하실 수 있어요.`,
          'success',
          'bottom',
        );
      }
    };

    const postProductInquiryCallMutation = useMutation((productId: number) => postProductInquiryCall(productId), {
      onSuccess: () => {},
      onError: () => {},
    });

    const onClickBtn = () => {
      if (actionFunction) {
        actionFunction();
      } else if (path) {
        navigate(path);
      } else {
        navigateBack();
      }
    };

    useEffect(() => {
      const contractWriterType = member?.id === chatRoomData?.seller.id ? TRANSFEROR : TRANSFEREE;

      if (!chatRoomData?.transferorCompleted && !chatRoomData?.transfereeCompleted) {
        setContractStatus(CONTRACT_STATUS_BEFORE);
      } else if (chatRoomData?.transferorCompleted && !chatRoomData?.transfereeCompleted) {
        setContractStatus(CONTRACT_STATUS_IN_PROGRESS);
      } else if (chatRoomData?.transferorCompleted && chatRoomData?.transfereeCompleted) {
        setContractStatus(CONTRACT_STATUS_COMPLETED);
      }

      setContractWriterType(contractWriterType);
    }, [chatRoomData]);

    const onClickMove = (e: React.MouseEvent, key: string) => {
      e.stopPropagation();
      if (key === 'contract') {
        if (chatRoomData?.id) {
          navigate(`/${key}`, { state: { chatRoomId: chatRoomData.id, contractWriterType } });
        } else {
          showToast(
            `${truncateText(recipient?.name, 3)}님에게 메시지를 보낸 후, 계약서를 작성할 수 있어요.`,
            'success',
            'bottom',
          );
        }
      } else {
        navigate(`/${key}`);
      }
    };

    const getContractStatusBtn = () => {
      switch (contractStatus) {
        case CONTRACT_STATUS_BEFORE:
          return (
            <>
              <PencilLineSoildIcon color={colors.grey[7]} width={18} height={18} />
              <span>안심 전자계약서 작성하기</span>
            </>
          );
        case CONTRACT_STATUS_IN_PROGRESS:
          if (isBuyer) {
            return (
              <>
                <PencilLineSoildIcon color={colors.grey[7]} width={18} height={18} />
                <span>안심 전자계약서 작성하기</span>
              </>
            );
          } else {
            return (
              <>
                <span>안심 전자계약서 확인하기</span>
              </>
            );
          }

        case CONTRACT_STATUS_COMPLETED:
          return (
            <>
              <span>안심 전자 계약서 보기</span>
            </>
          );
        default:
          return '안심 전자계약서 상태를 확인하세요.';
      }
    };

    return (
      <>
        <div
          ref={ref}
          className="fixed shrink-0 z-20 top-0 mx-auto left-0 right-0 w-full min-w-[280px] max-w-[720px] bg-white"
        >
          <div className="relative z-30 flex items-center justify-between p-4">
            <div className="flex">
              <div className="flex items-center justify-center mr-4 cursor-pointer" onClick={onClickBtn}>
                <ChevronLeftIcon color={colors.gray[9]}></ChevronLeftIcon>
              </div>
              <h1 className="flex items-center leading-[28px]">
                <span className="text-xl font-semibold text-gray-8">{truncateText(recipient?.name, 5)}</span>
                <span className="text-base tracking-wider text-gray-7 xxs:hidden">[{chatRoomData?.truckNumber}]</span>
              </h1>
            </div>
            <div className="flex">
              {isBuyer && chatRoomData?.productStatus?.code === SALE && !chatRoomData?.isDeletedProduct ? (
                <div className="cursor-pointer xxs:mx-1 xs:mx-1" onClick={onClickPhoneCall}>
                  <PhoneRingOutlineIcon color={colors.grey[8]} width={26} height={26} />
                </div>
              ) : (
                ''
              )}
              <div className="ml-2" onClick={onClickDeleteBtn}>
                <TrashIcon color={colors.gray[8]}></TrashIcon>
              </div>
            </div>
          </div>
          <div className="relative pt-1 border-b bg-gray-0">
            <div
              className={`flex relative min-w-[250px] px-4 ${
                isProductDeleted() ? 'text-gray-5 cursor-default pb-5' : ''
              } `}
              onClick={() => {
                if (!isProductDeleted()) {
                  navigate(`/products/${chatRoomData?.productId}`);
                }
              }}
            >
              <div className="h-[42px] w-[42px]">
                <img
                  src={chatRoomData?.productRepresentImageUrl}
                  alt="트럭이미지"
                  className="object-cover w-full h-full rounded-[8px]"
                ></img>
              </div>
              <div className="pl-[10px] flex-1 min-w-[160px]">
                <div className="flex items-center gap-[6px]">
                  <span className={`text-[14px] leading-[17px] font-bold break-keep`}>
                    {isProductDeleted() ? '삭제됨' : chatRoomData?.productStatus?.desc}
                  </span>

                  <span className="text-[14px] leading-[17px] font-medium text-grey-8 break-keep sm280:text-[13px] sm280:leading-[16px] text-ellipsis line-clamp-1">
                    {chatRoomData?.truckName}
                  </span>
                </div>

                <p className="mt-[6px] leading-[19px] text-[16px] sm280:leading-[18px] font-semibold text-grey-9">
                  {formatPrice(chatRoomData?.price)}
                </p>
              </div>
            </div>
            {!isProductDeleted() && (
              <div className="flex justify-end p-[10px]">
                <div
                  className="flex justify-center items-center gap-[6px] rounded-[10px] px-3 py-2 text-sm font-semibold w-full text-grey-9 border border-grey-4"
                  onClick={(e) => onClickMove(e, 'contract')}
                >
                  {getContractStatusBtn()}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  },
);

export default ChatRoomHeader;
